import { useEffect, useRef } from 'react'

const DEFAULT_SRC = 'https://res.cloudinary.com/dm6lvlhg3/video/upload/v1790681756/energy-sphere_plvm19.mp4'
const DEFAULT_POSTER = '/media/energy-sphere-poster.jpg'

/**
 * The ambient backdrop video, shared by the auth shell and the invite stage.
 * The invite stage keeps the original energy sphere; the auth shell passes
 * its own orb via `src`/`poster`. Playback follows the live
 * prefers-reduced-motion preference — the page CSS hides the element for
 * those users and keeps its static ambient glow as the fallback, so pausing
 * here just stops wasted decode work.
 */
export function AmbientVideo({
  className,
  src = DEFAULT_SRC,
  poster = DEFAULT_POSTER,
}: {
  className: string
  src?: string
  poster?: string
}) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => {
      if (mq.matches) {
        video.pause()
      } else {
        video.play().catch(() => { /* autoplay refusal is fine */ })
      }
    }
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  return (
    <video
      ref={videoRef}
      className={className}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      poster={poster}
      disablePictureInPicture
      aria-hidden="true"
      tabIndex={-1}
    >
      <source src={src} type="video/mp4" />
    </video>
  )
}
